import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, ArrowRight } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { SERVICES, buildWaLink, serviceMessage, trackWhatsApp } from "@/lib/services";
import { createEnquiry } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

export default function ContactSection() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", phone: "", area: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selectedService = SERVICES.find((s) => s.slug === form.service);
  const waForSelected = selectedService
    ? buildWaLink(serviceMessage(selectedService.name))
    : buildWaLink("Hi Velixa Health, I need help with home care for my family member.");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number so we can call you back.");
      return;
    }
    setLoading(true);
    try {
      await createEnquiry({
        name: form.name.trim(),
        phone: form.phone.trim(),
        service: selectedService?.name || undefined,
        location: form.area.trim() || "Bangalore",
        message: form.message.trim() || "Callback request from website.",
      });
      setSubmitted(true);
      toast.success("Callback requested. We'll ring you shortly.");
      setForm({ name: "", phone: "", area: "", service: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please call us on " + BRAND.phone);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="contact-section" id="contact" className="py-16 md:py-24 bg-bone">
      <div className="container-x grid grid-cols-12 gap-10">
        <div className="col-span-12 lg:col-span-5">
          <h2 className="text-[32px] md:text-[44px] font-extrabold text-navy leading-[1.1]">
            {t("contact.h2a")}
          </h2>
          <p className="mt-4 text-ink/85 text-[17px] max-w-md">{t("contact.sub")}</p>

          <div className="mt-8 space-y-3">
            <a data-testid="contact-phone-tile" href={`tel:${BRAND.phoneTel}`} className="flex items-center gap-3 bg-white rounded-2xl border border-navy/10 p-4 hover-care">
              <span className="icon-tile"><Phone className="w-5 h-5" /></span>
              <div><div className="text-[12px] uppercase tracking-widest text-brand font-bold">Phone</div><div className="text-navy font-bold text-[18px]">{BRAND.phoneDisplay}</div></div>
            </a>
            <a data-testid="contact-wa-tile" href={waForSelected} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp("Contact tile WhatsApp")} className="flex items-center gap-3 bg-white rounded-2xl border border-navy/10 p-4 hover-care">
              <span className="icon-tile" style={{ background: "#DCFCE7", color: "#1EB757" }}><MessageCircle className="w-5 h-5" /></span>
              <div><div className="text-[12px] uppercase tracking-widest text-brand font-bold">WhatsApp</div><div className="text-navy font-bold text-[18px]">wa.me/917483985244</div></div>
            </a>
            <a data-testid="contact-email-tile" href={`mailto:${BRAND.email}`} className="flex items-center gap-3 bg-white rounded-2xl border border-navy/10 p-4 hover-care">
              <span className="icon-tile"><Mail className="w-5 h-5" /></span>
              <div><div className="text-[12px] uppercase tracking-widest text-brand font-bold">Email</div><div className="text-navy font-bold text-[17px] break-all">{BRAND.email}</div></div>
            </a>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7">
          <form data-testid="enquiry-form" onSubmit={submit} className="bg-white rounded-3xl border border-navy/10 shadow-soft p-6 md:p-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Name" required>
                <Input data-testid="enquiry-name" value={form.name} onChange={(e) => onChange("name", e.target.value)} placeholder="Your full name" className="h-12 rounded-xl bg-bone border-navy/15 focus-visible:ring-brand" />
              </Field>
              <Field label="Phone (+91)" required>
                <Input data-testid="enquiry-phone" value={form.phone} onChange={(e) => onChange("phone", e.target.value)} placeholder="10-digit number" className="h-12 rounded-xl bg-bone border-navy/15 focus-visible:ring-brand" />
              </Field>
              <Field label="Area in Bangalore">
                <Input data-testid="enquiry-area" value={form.area} onChange={(e) => onChange("area", e.target.value)} placeholder="e.g. Whitefield, HSR Layout" className="h-12 rounded-xl bg-bone border-navy/15 focus-visible:ring-brand" />
              </Field>
              <Field label="Service">
                <Select value={form.service} onValueChange={(v) => onChange("service", v)}>
                  <SelectTrigger data-testid="enquiry-service" className="h-12 rounded-xl bg-bone border-navy/15 focus:ring-brand"><SelectValue placeholder="Choose a service" /></SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Message (optional)" className="md:col-span-2">
                <Textarea data-testid="enquiry-message" rows={3} value={form.message} onChange={(e) => onChange("message", e.target.value)} placeholder="Anything we should know?" className="rounded-xl bg-bone border-navy/15 focus-visible:ring-brand" />
              </Field>
            </div>

            <div className="mt-7 flex flex-wrap items-center justify-between gap-3">
              <p className="text-[13px] text-ink/70 max-w-md">Available 24/7 · We usually call back within minutes.</p>
              <div className="flex gap-3">
                <button data-testid="enquiry-submit" type="submit" disabled={loading || submitted} className="btn-care disabled:opacity-60">
                  {loading ? "Sending…" : submitted ? "Received · Thank you" : t("contact.request_cb")} <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </button>
                <a data-testid="enquiry-wa-send" href={waForSelected} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp(selectedService?.name || "Contact form WhatsApp")} className="btn-wa" aria-label="Send enquiry on WhatsApp">
                  <MessageCircle className="w-4 h-4" aria-hidden="true" /> {t("contact.send_wa")}
                </a>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-[13px] font-semibold text-navy mb-1.5">{label}{required && <span className="text-care"> *</span>}</div>
      {children}
    </label>
  );
}
